import React from "react";
import Link from "next/link";
import { v3, inkAccent } from "@/lib/theme";
import { TOTALS, VALUE_SERIES, PORTFOLIO, fmtEur, fmtPct } from "@/lib/data/mock";
import { Sparkline, Arrow } from "@/components/ui/charts";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

function V3HeroCard() {
  const ink = inkAccent();
  return (
    <div style={{ background: v3.surf, border: `1px solid ${v3.line}`, borderRadius: 16, overflow: "hidden", boxShadow: v3.intense ? `0 0 0 1px ${v3.accentBg}, 0 24px 80px -40px ${v3.accentDim}` : "none" }}>
      <div style={{ padding: "14px 22px", borderBottom: `1px solid ${v3.line}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="v3-pulse animate-pulse" style={{ width: 6, height: 6, borderRadius: 99, background: v3.accent, boxShadow: `0 0 8px ${v3.accent}` }} />
          <span style={{ font: `13px ${v3.sans}`, color: v3.fg }}>Übersicht</span>
        </div>
        <span style={{ font: `11px ${v3.mono}`, color: v3.dim }}>Heute · 17:42</span>
      </div>
      <div style={{ padding: 28 }}>
        <div style={{ font: `12px ${v3.sans}`, color: v3.dim }}>Portfoliowert</div>
        <div style={{ font: `500 56px ${v3.sans}`, color: v3.fg, letterSpacing: "-0.035em", marginTop: 6 }}>
          {fmtEur(TOTALS.wert)}
        </div>
        <div style={{ display: "flex", gap: 14, marginTop: 10, font: `13px ${v3.mono}`, alignItems: "center" }}>
          <span style={{ color: ink, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Arrow dir="up" size={9} /> {fmtPct(TOTALS.wertstgPct)}
          </span>
          <span style={{ color: v3.dim }}>{fmtEur(TOTALS.wertstg, { sign: true })} · 24M</span>
        </div>

        <div style={{ marginTop: 28, color: ink }}>
          <Sparkline data={VALUE_SERIES} height={80} width={520} stroke={ink} fill={v3.intense ? v3.accentBg : "rgba(250,250,250,0.04)"} strokeWidth={1.5} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 24 }}>
          {[
            ["Miete / mtl.", fmtEur(TOTALS.miete)],
            ["Rendite",      `${TOTALS.rendite.toFixed(2).replace(".", ",")} %`],
            ["Cashflow",     fmtEur(TOTALS.cf, { sign: true })],
          ].map((k, i) => (
            <div key={i} style={{ background: v3.surf2, border: `1px solid ${v3.line}`, borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ font: `11px ${v3.sans}`, color: v3.dim }}>{k[0]}</div>
              <div style={{ font: `500 22px ${v3.sans}`, color: v3.fg, marginTop: 6, letterSpacing: "-0.02em" }}>{k[1]}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 24, borderTop: `1px solid ${v3.line}`, paddingTop: 16 }}>
          {PORTFOLIO.slice(0, 4).map((o) => (
            <div key={o.id} style={{ display: "grid", gridTemplateColumns: "1fr 80px 60px 60px", gap: 10, padding: "10px 0", borderTop: `1px solid ${v3.line}`, font: `13px ${v3.sans}`, alignItems: "center" }}>
              <div>
                <div style={{ color: v3.fg }}>{o.label}</div>
                <div style={{ font: `11px ${v3.mono}`, color: v3.dim2 }}>{o.city}</div>
              </div>
              <span style={{ textAlign: "right", font: `13px ${v3.mono}`, color: v3.fg2 }}>{fmtEur(o.miete)}</span>
              <span style={{ textAlign: "right", font: `13px ${v3.mono}`, color: v3.dim }}>{o.rendite.toFixed(1).replace(".", ",")}%</span>
              <span style={{ textAlign: "right", font: `13px ${v3.mono}`, color: o.cashflow >= 0 ? ink : v3.dim }}>
                {o.cashflow >= 0 ? "+" : "−"}{Math.abs(o.cashflow)}€
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function V3Hero() {
  return (
    <motion.section 
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      style={{ padding: "120px 56px 96px", position: "relative", overflow: "hidden" }} className="rp-section"
    >
      {v3.intense && (
        <div style={{
          position: "absolute", top: -260, right: -160, width: 720, height: 720, pointerEvents: "none",
          background: `radial-gradient(circle, ${v3.accentDim} 0%, transparent 62%)`,
          filter: "blur(20px)", opacity: 0.55,
        }} />
      )}

      <div style={{ position: "relative", display: "flex", gap: 14, alignItems: "center", marginBottom: 48 }}>
        <div style={{ width: 8, height: 8, borderRadius: 99, background: v3.accent, boxShadow: `0 0 12px ${v3.accent}` }} />
        <div style={{ font: `12px ${v3.mono}`, color: v3.dim, letterSpacing: "0.04em", textTransform: "uppercase" }}>Für Eigentümer und Privatinvestoren</div>
      </div>

      <h1 className="rp-h1" style={{ position: "relative", font: `500 112px/0.96 ${v3.sans}`, color: v3.fg, margin: 0, letterSpacing: "-0.045em", maxWidth: 1280 }}>
        Das Cockpit für<br />
        dein Immobilien­vermögen.
      </h1>
      <p className="rp-hero-sub" style={{ position: "relative", font: `400 28px/1.3 ${v3.sans}`, color: v3.fg2, margin: "32px 0 0", maxWidth: 920, letterSpacing: "-0.015em" }}>
        Rendite, Cashflow und Portfolio — <span style={{ color: v3.accent }}>auf einen Blick.</span>
      </p>

      <div className="rp-stack" style={{ position: "relative", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, marginTop: 80, alignItems: "start" }}>
        <div>
          <p style={{ font: `400 17px/1.55 ${v3.sans}`, color: v3.dim, margin: 0, maxWidth: 460 }}>
            Du sitzt am Steuer deines Vermögens — nicht dein Steuerberater, nicht die Bank, nicht der Zufall. realpilot rechnet, sortiert und exportiert. Du entscheidest.
          </p>
          <div data-rp-cta-row style={{ marginTop: 32, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <Link href="/register">
              <Button size="lg">
                3 Objekte gratis testen →
              </Button>
            </Link>
            <Link href="/login">
              <Button variant="outline" size="lg">
                Demo ansehen
              </Button>
            </Link>
          </div>
          <div className="rp-hero-stats" style={{ marginTop: 56, display: "flex", gap: 56, flexWrap: "wrap" }}>
            {[
              ["3 Min.",  "pro Objekt"],
              ["42 Sek.", "Bankenpräsentation"],
              ["0 €",     "bis Objekt 3"],
            ].map((k, i) => (
              <div key={i}>
                <div style={{ font: `500 36px ${v3.sans}`, color: v3.fg, letterSpacing: "-0.03em" }}>{k[0]}</div>
                <div style={{ font: `13px ${v3.sans}`, color: v3.dim, marginTop: 4 }}>{k[1]}</div>
              </div>
            ))}
          </div>
        </div>
        <V3HeroCard />
      </div>
    </motion.section>
  );
}

export function V3TaglineBand() {
  return (
    <motion.section 
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      style={{
        padding: "96px 56px", textAlign: "center",
        borderTop: `1px solid ${v3.line}`, borderBottom: `1px solid ${v3.line}`,
        background: v3.bg, position: "relative", overflow: "hidden",
      }} className="rp-section"
    >
      {v3.intense && (
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: `radial-gradient(ellipse at center, ${v3.accentBg} 0%, transparent 60%)`,
          opacity: 0.6,
        }} />
      )}
      <div style={{ position: "relative", font: `12px ${v3.mono}`, color: v3.accent, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 22 }}>
        Profi-Werkzeug
      </div>
      <h2 className="rp-tagline-h" style={{ position: "relative", font: `500 64px/1.05 ${v3.sans}`, color: v3.fg, margin: "0 auto", letterSpacing: "-0.035em", maxWidth: 1100 }}>
        Die Tools mit denen Profis arbeiten.<br />
        <span style={{ color: v3.accent }}>Jetzt auch für dich.</span>
      </h2>
    </motion.section>
  );
}
